import { useSupportSheet } from "../components/SupportSheet";
import { memo } from "react";
import { useStore } from "../store/globalState";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  Button,
  CashSpecialIcon,
  GradientText,
  Layout,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { ConsumerSessionMenu } from "../components/consumer-auth/ConsumerSessionMenu";
import { CONSUMER_AUTH_ROUTES, FUND_REQUEST_ROUTES, TITLE_COLOR } from "../constants";
import { formatLabeledAmount } from "../utils";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";

/**
 * Gallery-safe "Fund request" screen.
 *
 * Chrome: Need help header (no back), left-aligned requester title, white
 * cash disc, requested amount, then Descartar (text) / Después (outline) /
 * Pagar (filled) stacked. Same CTA stack on payout-claim and both widgets.
 * Requester name and amount are placeholders — in production they come
 * from the claimed link.
 */
const REQUESTER_NAME_PLACEHOLDER = "Sofia Ospina";
const REQUESTED_AMOUNT = 2289.9;
const REQUESTED_CURRENCY = "GTQ";

export const FundRequestPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    backgroundIcon,
  } = colorConfig;
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("fund-request");

  const formattedAmount = formatLabeledAmount(
    REQUESTED_AMOUNT,
    REQUESTED_CURRENCY
  );

  return (
    <>
    <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
      <Layout.Header>
        <ConsumerSessionMenu />
        <Navbar
          title={t("common.needHelp", { ns: "fund-request" })}
          align="right"
          noBackButton
          onIconClick={openSupport}
          icon={<SupportIcon size={35} colors={dropzoneColors} />}
        />
      </Layout.Header>
      <Layout.Content className="landing-section fund-request-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <Title
          title={t("fundRequest.requestedYou", { name: REQUESTER_NAME_PLACEHOLDER })}
          align="left"
          color={TITLE_COLOR}
        />
        <section className="fund-request-hero">
          <div className="fund-request-disc">
            <FlowHeroMark
              dropzoneColors={dropzoneColors}
              backgroundIcon={backgroundIcon}
            >
              <CashSpecialIcon size={60} colors={dropzoneColors} />
            </FlowHeroMark>
          </div>

          <section className="landing-container fund-request-amount">
            <GradientText
              as="label"
              textColor={TITLE_COLOR}
              className="fund-request-label"
            >
              {t("fundRequest.requestedAmount")}
            </GradientText>
            <GradientText as="h1" textColor={TITLE_COLOR}>
              {formattedAmount}
            </GradientText>
          </section>
        </section>
      </Layout.Content>

      <Layout.Footer>
        <section className="footer-section">
          <Button
            type="button"
            variant="cancel"
            size="small"
            color={TITLE_COLOR}
            className="fund-request-discard"
            onClick={() => navigate(FUND_REQUEST_ROUTES.DISCARD)}
          >
            {t("fundRequest.discard")}
          </Button>

          <section className="fund-request-actions fund-request-actions--stack">
            <Button
              type="button"
              size="large"
              variant="outline"
              background={backgroundBtn}
              color={TITLE_COLOR}
              style={{ backgroundColor: background }}
              onClick={() => navigate(FUND_REQUEST_ROUTES.LATER)}
            >
              {t("fundRequest.later")}
            </Button>
            <Button
              type="button"
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => navigate(CONSUMER_AUTH_ROUTES.SIGN_IN)}
            >
              {t("fundRequest.pay")}
            </Button>
          </section>
        </section>
      </Layout.Footer>
    </AuraLayout>
    {supportSheet}
  </>
  );
});
